import React, { useState, useEffect } from 'react';
import { getAdminRoles, createAdminRole, updateAdminRole, deleteAdminRole, getPermissionMatrix, setRolePermissions, getRoleScopes, setRoleScopes } from '../../services/api';
import { FiKey, FiPlus, FiEdit2, FiTrash2, FiShield, FiUsers } from 'react-icons/fi';

const RolesManagement: React.FC = () => {
    const [tab, setTab] = useState<'roles' | 'matrix' | 'scopes'>('roles');
    const [roles, setRoles] = useState<any[]>([]);
    const [matrix, setMatrix] = useState<any>(null);
    const [showModal, setShowModal] = useState(false);
    const [editRole, setEditRole] = useState<any>(null);
    const [form, setForm] = useState({ nom: '', description: '', niveau: 50 });
    const [matrixChanges, setMatrixChanges] = useState<Record<string, number[]>>({});
    const [scopeData, setScopeData] = useState<any>({});
    const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        if (tab === 'matrix') loadMatrix();
    }, [tab]);

    const loadRoles = () => {
        getAdminRoles().then(res => setRoles(res.data));
    };

    const loadMatrix = () => {
        getPermissionMatrix().then(res => {
            setMatrix(res.data);
            setMatrixChanges(res.data.matrix || {});
        });
    };

    const loadScopes = (roleId: number) => {
        setSelectedRoleId(roleId);
        getRoleScopes(roleId).then(res => {
            const scopeMap: any = {};
            res.data.forEach((s: any) => { scopeMap[s.module_code] = s.scope_type; });
            setScopeData(scopeMap);
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (editRole) await updateAdminRole(editRole.id, form);
            else await createAdminRole(form);
            setShowModal(false); setEditRole(null); loadRoles();
        } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Supprimer ce role ?')) {
            try {
                await deleteAdminRole(id);
                loadRoles();
            } catch (err: any) { alert(err.response?.data?.error || 'Erreur'); }
        }
    };

    const togglePermission = (roleId: number, permId: number) => {
        const key = String(roleId);
        const current = matrixChanges[key] || [];
        const newPerms = current.includes(permId)
            ? current.filter(id => id !== permId)
            : [...current, permId];
        setMatrixChanges({ ...matrixChanges, [key]: newPerms });
    };

    const saveMatrixForRole = async (roleId: number) => {
        await setRolePermissions(roleId, { permission_ids: matrixChanges[String(roleId)] || [] });
        alert('Permissions sauvegardees');
    };

    const saveScopes = async () => {
        if (!selectedRoleId) return;
        const scopes = Object.entries(scopeData).map(([module_code, scope_type]) => ({ module_code, scope_type }));
        await setRoleScopes(selectedRoleId, { scopes });
        alert('Perimetres sauvegardes');
    };

    return (
        <div className="page-roles">
            <div className="page-header">
                <h2><FiKey /> Roles & Permissions</h2>
                <button className="btn btn-primary" onClick={() => { setEditRole(null); setForm({ nom: '', description: '', niveau: 50 }); setShowModal(true); }}><FiPlus /> Nouveau role</button>
            </div>

            <div className="tabs">
                <button className={`tab ${tab === 'roles' ? 'active' : ''}`} onClick={() => setTab('roles')}>Roles</button>
                <button className={`tab ${tab === 'matrix' ? 'active' : ''}`} onClick={() => setTab('matrix')}>Matrice Permissions</button>
                <button className={`tab ${tab === 'scopes' ? 'active' : ''}`} onClick={() => setTab('scopes')}>Perimetres d'acces</button>
            </div>

            {tab === 'roles' && (
                <div className="cards-grid">
                    {roles.map(r => (
                        <div key={r.id} className="rule-card">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h4>{r.nom}</h4>
                                <div className="actions">
                                    {!r.systeme && <button className="btn-icon" onClick={() => { setEditRole(r); setForm({ nom: r.nom, description: r.description || '', niveau: r.niveau }); setShowModal(true); }}><FiEdit2 /></button>}
                                    {!r.systeme && <button className="btn-icon danger" onClick={() => handleDelete(r.id)}><FiTrash2 /></button>}
                                </div>
                            </div>
                            <p className="text-muted">{r.description || 'Aucune description'}</p>
                            <div className="rule-meta">
                                <span className="badge"><FiUsers /> {r.user_count || 0} utilisateurs</span>
                                <span className="badge badge-outline"><FiShield /> {r.perm_count || 0} permissions</span>
                                <span className="badge badge-purple">Niveau {r.niveau}</span>
                            </div>
                            {r.systeme ? <span className="status active">Systeme</span> : <span className="status">{r.actif ? 'Actif' : 'Inactif'}</span>}
                        </div>
                    ))}
                </div>
            )}

            {tab === 'matrix' && matrix && (
                <div style={{ overflowX: 'auto' }}>
                    {matrix.roles?.map((role: any) => (
                        <div key={role.id} className="chart-card" style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                <h3>{role.nom}</h3>
                                <button className="btn btn-sm btn-primary" onClick={() => saveMatrixForRole(role.id)}>Sauvegarder</button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                                {matrix.modules?.map((mod: any) => (
                                    <div key={mod.code} style={{ minWidth: 200, padding: 12, background: 'var(--bg)', borderRadius: 8 }}>
                                        <strong style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{mod.nom}</strong>
                                        <div style={{ marginTop: 8 }}>
                                            {mod.permissions?.map((perm: any) => {
                                                const checked = (matrixChanges[String(role.id)] || []).includes(perm.id);
                                                return (
                                                    <label key={perm.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 4, cursor: 'pointer' }}>
                                                        <input type="checkbox" checked={checked} onChange={() => togglePermission(role.id, perm.id)} />
                                                        {perm.action}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === 'scopes' && (
                <div>
                    <div className="filters-bar">
                        <select value={selectedRoleId || ''} onChange={e => loadScopes(Number(e.target.value))}>
                            <option value="">-- Selectionner un role --</option>
                            {roles.map(r => <option key={r.id} value={r.id}>{r.nom}</option>)}
                        </select>
                        {selectedRoleId && <button className="btn btn-primary" onClick={saveScopes}>Sauvegarder</button>}
                    </div>
                    {selectedRoleId && matrix && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead><tr><th>Module</th><th>Perimetre</th></tr></thead>
                                <tbody>
                                    {matrix?.modules?.map((mod: any) => (
                                        <tr key={mod.code}>
                                            <td><strong>{mod.nom}</strong></td>
                                            <td>
                                                <select value={scopeData[mod.code] || 'own'} onChange={e => setScopeData({ ...scopeData, [mod.code]: e.target.value })}>
                                                    <option value="all">Tout voir</option>
                                                    <option value="zone">Sa zone</option>
                                                    <option value="team">Son equipe</option>
                                                    <option value="own">Ses donnees</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>{editRole ? 'Modifier' : 'Nouveau'} Role</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-group"><label>Nom *</label><input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required /></div>
                            <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
                            <div className="form-group"><label>Niveau hierarchique</label><input type="number" value={form.niveau} onChange={e => setForm({ ...form, niveau: Number(e.target.value) })} min={0} max={100} /></div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                                <button type="submit" className="btn btn-primary">{editRole ? 'Mettre a jour' : 'Creer'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RolesManagement;
